import { STATUS, type StatusKey, fmt } from "@/lib/data";
import { useIsMobile } from "@/lib/useViewport";
// ui.tsx — shared UI primitives for the CMS (Wanted Design System).
import { type CSSProperties, type ReactNode, useState } from "react";

// ---- Number display ----
// 통화/수치 표기 규칙: 우측 정렬 + tabular-nums, 색은 의미가 있을 때만 —
// 녹색 = 이익/통과, 빨강 = 손실/미달. tone="auto" 는 부호를 따라간다.
export type NumTone = "auto" | "pos" | "neg" | "none";
export const numColor = (v: number, tone: NumTone): string | undefined => {
  if (tone === "pos") return "var(--num-positive)";
  if (tone === "neg") return "var(--num-negative)";
  if (tone === "auto")
    return v < 0 ? "var(--num-negative)" : v > 0 ? "var(--num-positive)" : undefined;
  return undefined;
};
export const Num = ({
  v,
  tone = "none",
  strong,
  suffix,
  style,
}: {
  v: number;
  tone?: NumTone;
  strong?: boolean;
  suffix?: string;
  style?: CSSProperties;
}) => (
  <span
    style={{
      fontVariantNumeric: "tabular-nums",
      fontWeight: strong ? 700 : undefined,
      color: numColor(v, tone),
      whiteSpace: "nowrap",
      ...style,
    }}
  >
    {fmt(v)}
    {suffix && <span style={{ fontWeight: 400 }}> {suffix}</span>}
  </span>
);

// ---- Data-dense table styles (Attio-style) ----
// 고밀도 그리드 공통 셀 스타일 — 1px 헤어라인, 고대비 헤더, 합계는 강한 윗선.
// 화면별 thStyle/tdStyle 중복 정의 대신 이걸 spread 해서 쓴다.
export const thBase: CSSProperties = {
  textAlign: "right",
  padding: "10px 14px",
  fontSize: 11.5,
  fontWeight: 700,
  color: "var(--text-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  background: "var(--table-header-bg)",
  borderBottom: "1px solid var(--table-border-strong)",
  whiteSpace: "nowrap",
};
export const tdBase: CSSProperties = {
  padding: "9px 14px",
  fontSize: 13,
  color: "var(--text-strong)",
  borderBottom: "1px solid var(--table-border)",
  whiteSpace: "nowrap",
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
};
export const tdLabelBase: CSSProperties = {
  ...tdBase,
  textAlign: "left",
  color: "var(--text-base)",
  fontWeight: 700,
};
export const totalCellBase: CSSProperties = {
  ...tdBase,
  color: "var(--text-base)",
  fontWeight: 800,
  borderTop: "2px solid var(--table-border-strong)",
  borderBottom: 0,
};

// ---- Horizontal scroll wrapper for data-dense tables ----
// CMS tables use whiteSpace:nowrap and many columns; on a phone they exceed the
// viewport. Wrapping a <table> here lets it scroll sideways instead of breaking
// the page layout. The negative margin + matching padding lets the scroll area
// bleed to the screen edges on mobile so the table isn't boxed by card padding.
export const TableScroll = ({
  children,
  style,
  bleed,
}: {
  children: ReactNode;
  style?: CSSProperties;
  // bleed: px of horizontal padding to cancel so the scroller reaches the edge.
  bleed?: number;
}) => {
  const m = bleed ?? 0;
  return (
    <div
      style={{
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        margin: m ? `0 -${m}px` : undefined,
        padding: m ? `0 ${m}px` : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

// ---- Lucide-style line icons ----
export type IconName =
  | "home"
  | "grid"
  | "list"
  | "plus"
  | "chart"
  | "db"
  | "search"
  | "user"
  | "chevL"
  | "chevR"
  | "chevD"
  | "download"
  | "file"
  | "copy"
  | "trash"
  | "clock"
  | "history"
  | "paperclip"
  | "check"
  | "x"
  | "alert"
  | "filter"
  | "calendar"
  | "eye"
  | "arrowRight"
  | "arrowUp"
  | "arrowDown"
  | "save"
  | "logout"
  | "settings"
  | "trend"
  | "layers"
  | "sun"
  | "moon"
  | "message"
  | "dot";

const PATHS: Record<IconName, ReactNode> = {
  home: (
    <>
      <path d="M3 12 12 3l9 9" />
      <path d="M5 10v10h14V10" />
    </>
  ),
  grid: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </>
  ),
  list: (
    <>
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </>
  ),
  plus: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </>
  ),
  chart: (
    <>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </>
  ),
  db: (
    <>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21v-1a7 7 0 0 1 14 0v1" />
    </>
  ),
  chevL: <path d="m15 18-6-6 6-6" />,
  chevR: <path d="m9 18 6-6-6-6" />,
  chevD: <path d="m6 9 6 6 6-6" />,
  download: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </>
  ),
  file: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </>
  ),
  copy: (
    <>
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </>
  ),
  trash: (
    <>
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  history: (
    <>
      <path d="M3 3v5h5" />
      <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
      <path d="M12 7v5l4 2" />
    </>
  ),
  paperclip: (
    <path d="M21.44 11.05l-9.19 9.19a5 5 0 0 1-7.07-7.07l9.19-9.19a3.5 3.5 0 0 1 4.95 4.95l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  ),
  check: <path d="M20 6 9 17l-5-5" />,
  x: (
    <>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </>
  ),
  alert: (
    <>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ),
  filter: <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />,
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  arrowRight: (
    <>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </>
  ),
  arrowUp: (
    <>
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </>
  ),
  arrowDown: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="19 12 12 19 5 12" />
    </>
  ),
  save: (
    <>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </>
  ),
  logout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
  trend: (
    <>
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </>
  ),
  layers: (
    <>
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </>
  ),
  moon: <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z" />,
  message: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  dot: <circle cx="12" cy="12" r="4" />,
};

export const Icon = ({
  name,
  size = 18,
  stroke = 2,
  color = "currentColor",
  style,
}: {
  name: IconName;
  size?: number;
  stroke?: number;
  color?: string;
  style?: CSSProperties;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, ...style }}
    aria-hidden="true"
  >
    {PATHS[name]}
  </svg>
);

// ---- Pill button (Wanted rounded-rect CTA) ----
type PillVariant = "primary" | "dark" | "light" | "outlined" | "ghost";
type PillSize = "sm" | "md" | "lg";
export const Pill = ({
  variant = "primary",
  size = "md",
  upper = false,
  children,
  onClick,
  icon,
  disabled,
  style,
}: {
  variant?: PillVariant;
  size?: PillSize;
  upper?: boolean;
  children?: ReactNode;
  onClick?: () => void;
  icon?: ReactNode;
  disabled?: boolean;
  style?: CSSProperties;
}) => {
  const [h, setH] = useState(false);
  const [p, setP] = useState(false);
  const sizes: Record<PillSize, CSSProperties> = {
    sm: { padding: "7px 14px", fontSize: 13, borderRadius: 10 },
    md: { padding: "10px 18px", fontSize: 14, borderRadius: 12 },
    lg: { padding: "13px 26px", fontSize: 15, borderRadius: 14 },
  };
  const variants: Record<PillVariant, CSSProperties> = {
    primary: { background: "var(--brand)", color: "#ffffff" },
    dark: { background: "var(--bg-surface-2)", color: "var(--text-base)" },
    light: { background: "var(--bg-inverse)", color: "var(--text-inverse)" },
    outlined: {
      background: "transparent",
      color: "var(--text-base)",
      boxShadow: "inset 0 0 0 1px var(--ring-input)",
    },
    ghost: { background: "transparent", color: "var(--text-muted)" },
  };
  const hov: CSSProperties =
    !h || disabled
      ? {}
      : variant === "primary"
        ? { background: "var(--brand-hover)" }
        : variant === "dark"
          ? { background: "var(--bg-surface-3)" }
          : variant === "light"
            ? { filter: "brightness(0.92)" }
            : variant === "outlined"
              ? { background: "var(--bg-hover-soft)" }
              : { background: "var(--bg-hover-soft)", color: "var(--text-base)" };
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => {
        setH(false);
        setP(false);
      }}
      onMouseDown={() => setP(true)}
      onMouseUp={() => setP(false)}
      style={{
        fontFamily: "inherit",
        fontWeight: 600,
        border: 0,
        letterSpacing: upper ? "1.6px" : "-0.01em",
        textTransform: upper ? "uppercase" : "none",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        whiteSpace: "nowrap",
        lineHeight: 1,
        transition:
          "background 120ms cubic-bezier(.3,0,0,1), transform 120ms cubic-bezier(.3,0,0,1)",
        transform: p && !disabled ? "scale(0.98)" : "scale(1)",
        ...sizes[size],
        ...variants[variant],
        ...hov,
        ...style,
      }}
    >
      {icon}
      {children}
    </button>
  );
};

// ---- Status badge ----
export const StatusBadge = ({ status, size = "md" }: { status: StatusKey; size?: "sm" | "md" }) => {
  const s = STATUS[status] || STATUS.draft;
  const small = size === "sm";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: s.bg,
        color: s.color,
        fontSize: small ? 11.5 : 12.5,
        fontWeight: 700,
        padding: small ? "3px 9px" : "4px 12px",
        borderRadius: 9999,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.color }} />
      {s.label}
    </span>
  );
};

// ---- Generic surface card ----
export const Card = ({
  children,
  style,
  pad = 24,
  hover,
  onClick,
}: {
  children: ReactNode;
  style?: CSSProperties;
  pad?: number;
  hover?: boolean;
  onClick?: () => void;
}) => {
  const [h, setH] = useState(false);
  const mobile = useIsMobile();
  // Tight phone screens can't spare 24px gutters — clamp roomy card padding.
  const effPad = mobile && pad > 16 ? 16 : pad;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => hover && setH(true)}
      onMouseLeave={() => hover && setH(false)}
      style={{
        background: h ? "var(--bg-card-hover)" : "var(--bg-surface)",
        borderRadius: 16,
        padding: effPad,
        boxShadow: h ? "rgba(0,0,0,0.16) 0px 8px 20px" : "none",
        transition: "background 120ms, box-shadow 120ms",
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >
      {children}
    </div>
  );
};

// ---- Form field label ----
export const Field = ({
  label,
  en,
  required,
  children,
  hint,
  style,
}: {
  label: string;
  en?: string;
  required?: boolean;
  children: ReactNode;
  hint?: string;
  style?: CSSProperties;
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 7, ...style }}>
    <label
      style={{
        fontSize: 12.5,
        color: "var(--text-muted)",
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {label}
      {en && (
        <span style={{ color: "var(--text-fainter)", fontWeight: 400, letterSpacing: "0.5px" }}>
          {en}
        </span>
      )}
      {required && <span style={{ color: "var(--brand)" }}>*</span>}
    </label>
    {children}
    {hint && <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>{hint}</span>}
  </div>
);

// ---- Text input (recessed inset border) ----
export const TextInput = ({
  value,
  onChange,
  placeholder,
  type = "text",
  align = "left",
  suffix,
  style,
  mono,
  disabled,
}: {
  value: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  type?: string;
  align?: "left" | "right" | "center";
  suffix?: ReactNode;
  style?: CSSProperties;
  mono?: boolean;
  disabled?: boolean;
}) => {
  const [f, setF] = useState(false);
  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        background: "var(--bg-surface-2)",
        borderRadius: 12,
        height: 44,
        padding: "0 14px",
        boxShadow: f
          ? "var(--ring-focus) 0px 0px 0px 2px inset"
          : "var(--ring-input) 0px 0px 0px 1px inset",
        transition: "box-shadow 120ms",
        opacity: disabled ? 0.55 : 1,
        ...style,
      }}
    >
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => setF(true)}
        onBlur={() => setF(false)}
        style={{
          background: "transparent",
          border: 0,
          outline: 0,
          color: "var(--text-base)",
          fontFamily: mono ? "ui-monospace, 'SF Mono', Menlo, monospace" : "inherit",
          fontSize: 14,
          width: "100%",
          textAlign: align,
        }}
      />
      {suffix && (
        <span style={{ color: "var(--text-muted)", fontSize: 13, marginLeft: 6 }}>{suffix}</span>
      )}
    </div>
  );
};

// ---- Multi-line text input (same recessed inset border as TextInput) ----
export const Textarea = ({
  value,
  onChange,
  placeholder,
  rows = 3,
  maxLength,
  style,
}: {
  value: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  style?: CSSProperties;
}) => {
  const [f, setF] = useState(false);
  return (
    <textarea
      value={value}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      onChange={(e) => onChange?.(e.target.value)}
      onFocus={() => setF(true)}
      onBlur={() => setF(false)}
      style={{
        background: "var(--bg-surface-2)",
        borderRadius: 12,
        border: 0,
        outline: 0,
        padding: "12px 14px",
        color: "var(--text-base)",
        fontFamily: "inherit",
        fontSize: 14,
        lineHeight: 1.5,
        width: "100%",
        resize: "vertical",
        boxShadow: f
          ? "var(--ring-focus) 0px 0px 0px 2px inset"
          : "var(--ring-input) 0px 0px 0px 1px inset",
        transition: "box-shadow 120ms",
        ...style,
      }}
    />
  );
};

// ---- Select ----
export type SelectOption = string | { value: string; label: string };
export const Select = ({
  value,
  onChange,
  options,
  placeholder,
  style,
}: {
  value: string;
  onChange?: (v: string) => void;
  options: SelectOption[];
  placeholder?: string;
  style?: CSSProperties;
}) => {
  const [f, setF] = useState(false);
  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        background: "var(--bg-surface-2)",
        borderRadius: 12,
        height: 44,
        padding: "0 12px 0 14px",
        boxShadow: f
          ? "var(--ring-focus) 0px 0px 0px 2px inset"
          : "var(--ring-input) 0px 0px 0px 1px inset",
        transition: "box-shadow 120ms",
        ...style,
      }}
    >
      <select
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => setF(true)}
        onBlur={() => setF(false)}
        style={{
          background: "transparent",
          border: 0,
          outline: 0,
          color: value ? "var(--text-base)" : "var(--text-muted)",
          fontFamily: "inherit",
          fontSize: 14,
          width: "100%",
          appearance: "none",
          WebkitAppearance: "none",
          cursor: "pointer",
        }}
      >
        {placeholder && (
          <option value="" style={{ background: "var(--bg-surface-2)" }}>
            {placeholder}
          </option>
        )}
        {options.map((o) => {
          const val = typeof o === "string" ? o : o.value;
          const lab = typeof o === "string" ? o : o.label;
          return (
            <option
              key={val}
              value={val}
              style={{ background: "var(--bg-surface-2)", color: "var(--text-base)" }}
            >
              {lab}
            </option>
          );
        })}
      </select>
      <span
        style={{
          position: "absolute",
          right: 12,
          pointerEvents: "none",
          color: "var(--text-muted)",
          display: "flex",
        }}
      >
        <Icon name="chevD" size={16} />
      </span>
    </div>
  );
};

// ---- Section heading ----
export const SectionTitle = ({
  children,
  sub,
  right,
}: {
  children: ReactNode;
  sub?: string;
  right?: ReactNode;
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      marginBottom: 16,
    }}
  >
    <div>
      <div
        style={{
          fontFamily: "var(--font-title)",
          fontWeight: 800,
          fontSize: 22,
          letterSpacing: "-0.01em",
        }}
      >
        {children}
      </div>
      {sub && <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>{sub}</div>}
    </div>
    {right}
  </div>
);
